// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState, useEffect, useCallback } from 'react';
import { getChecklist } from '../services/coverageService';
import type { ChecklistResponse } from '../types';

/** How often to poll the checklist while the documents step is visible.
 * Classification results land asynchronously (workflow ingest), so the
 * checklist refreshes in the background; mutations call refetch() directly. */
const POLL_INTERVAL_MS = 10_000;

interface UseCoverageChecklistResult {
  checklist: ChecklistResponse | null;
  loading: boolean;
  error: string | null;
  /** True when the session no longer exists server-side (404). Terminal:
   * polling stops and the caller should tell the user to start over — with
   * the in-memory session store, a service restart invalidates open
   * sessions and silently retaining stale data hides that. */
  sessionGone: boolean;
  refetch: () => void;
}

export function useCoverageChecklist(
  siteId: string | undefined,
  sessionId: string | undefined,
  enabled = true
): UseCoverageChecklistResult {
  const [checklist, setChecklist] = useState<ChecklistResponse | null>(null);
  const [loading, setLoading] = useState(enabled && Boolean(siteId && sessionId));
  const [error, setError] = useState<string | null>(null);
  const [sessionGone, setSessionGone] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey(k => k + 1), []);

  // The terminal state belongs to the ids that 404'd, not the hook instance:
  // a new session (or site) must start fresh and poll normally.
  useEffect(() => {
    setSessionGone(false);
  }, [siteId, sessionId]);

  useEffect(() => {
    if (!enabled || !siteId || !sessionId || sessionGone) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    // `isPoll` distinguishes the background refresh from the initial/manual
    // load: a poll must never flash the loading spinner or blow away a
    // previously-successful checklist with an error banner over a transient
    // blip (same pattern as useApprovals). A 404 is the exception — the
    // session is gone for good, so it is surfaced even from a poll.
    const load = async (isPoll: boolean) => {
      try {
        if (!isPoll) {
          setLoading(true);
          setError(null);
        }
        const result = await getChecklist(siteId, sessionId);
        if (cancelled) return;
        setChecklist(result);
        if (isPoll) setError(null);
      } catch (err) {
        if (cancelled) return;
        if ((err as { status?: number }).status === 404) {
          setSessionGone(true);
          return;
        }
        if (isPoll) return;
        setError((err as Error).message ?? 'Failed to load checklist');
      } finally {
        if (!cancelled && !isPoll) setLoading(false);
      }
    };

    load(false);
    const intervalId = setInterval(() => load(true), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [siteId, sessionId, enabled, fetchKey, sessionGone]);

  return { checklist, loading, error, sessionGone, refetch };
}
